import queryString from 'query-string'
import axios from 'axios'
import { UAParser } from 'ua-parser-js'
import { app, api, routes } from 'config'
import { constants } from 'utils'
import appConfig from 'config/app-config'

/**
 * Logs error in Cloud Watch
 * @param {*} error - error information
 * @param {string} module - name of the module in which error occurred
 * @param {string} [moduleDescription] - more detail about the module
 */
export const logError = (
  error: any,
  module: string,
  moduleDescription?: string | null,
) => {
  if (app.disableLoggingErrorService) return

  const ua = new UAParser(navigator.userAgent)

  return axios.post(api.logError(), {
    application: 'SSO - Custom Login',
    applicationURI: window.location.href,
    module,
    moduleDescription,
    userAgent: ua.getResult(),
    user: storage.getUser(),
    error,
  })
}

/**
 * Redirect user after authentication either to caller site or default site
 * @param {*} location
 */
export const redirect = (location: any) => {
  const values = queryString.parse(location.search)

  // keep track of possible login loop and propagate its value when doing redirect
  let loopCountVal = ''
  if (values.loopCount != null) {
    loopCountVal = '&loopCount=' + values.loopCount
  }

  if (values.fromURI != null) {
    // fromURL will be provided when the user initiates login from a SAML application
    document.location.href = (values.fromURI as string) + loopCountVal
  } else if (values.state != null) {
    // state is used by us to persist the application that initiated the logout request, so that a subsequent login
    // will return them to the same application
    document.location.href = (values.state as string) + loopCountVal
  } else {
    // User accessed login page directly... redirect to WWW
    document.location.href = appConfig.isMultiLogin
      ? `${routes.multiLogin}/login`
      : routes.BCOM.login
  }
}

/**
 * Returns date in UTC format
 * E.g. 2020/07/20
 * @param date
 */
export const getDateInUTC = (date = new Date()): string => {
  const utcDate = date.toISOString().split('T')[0]
  const formatedDate = utcDate.split('-').join('/')
  return formatedDate
}

/**
 * Remove focus from field while form submission
 */
export const removeFocusFromField = () =>
  document.getElementById('focusField')?.focus()

/**
 * Getting/Setting item in/from session storage
 */
export const storage: any = {
  setUser(user = '') {
    sessionStorage.setItem(constants.BROWSER_STORAGE_KEY.USER, user)
  },
  getUser() {
    return sessionStorage.getItem(constants.BROWSER_STORAGE_KEY.USER)
  },
}

/***
 *
 * setCookie used for set Cookie in the browser
 */
export const setCookie = (
  cName: string,
  value: string,
  exdays: number | null,
) => {
  const exdate = new Date()
  exdate.setDate(exdate.getDate() + (exdays ?? 0))
  const cookieDomain = '.' + document.domain.split('.').splice(1).join('.')
  const cValue =
    escape(value) +
    (exdays === null ? '' : '; expires=' + exdate.toUTCString()) +
    ';domain=' +
    cookieDomain +
    ';path=/'
  document.cookie = cName + '=' + cValue
}
/**
 *
 * @func getCookieVal - used to get the value
 *
 */
export const getCookieVal = (name: string) => {
  const value: any = '; ' + document.cookie
  const parts = value.split('; ' + name + '=')
  if (parts.length === 2) return parts.pop().split(';').shift()
}
/**
 *
 * @param name - it is string key to delete particular key cookie
 * @param domain - optional explicit domain (e.g., '.brightmls.com'); if not provided, uses dynamic domain matching setCookie
 */
export const deleteCookie = (name: string, domain?: string) => {
  let domainAttr = ''

  if (domain) {
    domainAttr = `Domain=${domain};`
  } else {
    const docDomain = document.domain || ''
    if (docDomain && docDomain !== 'localhost') {
      const cookieDomain = '.' + docDomain.split('.').splice(1).join('.')
      if (cookieDomain !== '.') {
        domainAttr = `Domain=${cookieDomain};`
      }
    }
  }

  document.cookie = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;${domainAttr}`
}

export function isExternalLink(url: string) {
  if (url) {
    return url.includes('http://') || url.includes('https://')
  }
  return false
}
export const routeHandler = (e: any, link: string, id?: string) => {
  e.preventDefault()
  //isExternalLink  returns true if the link has the http and https in the string
  if (id) {
    window.open(`${link}/${id}`, '_blank')
  } else {
    window.open(`${link}`, '_blank')
  }
}
export function createMarkup(e: any) {
  return { __html: e }
}

/**
 * Determines text to display for autocomplete search control
 * @param searchText search text being used
 * @param isLoading indicator whether or not search is being performed
 * @returns text to display in autocomplete search control
 */
export const noOptionsText = (searchText: string, isLoading: boolean) => {
  if (searchText.length > 3) {
    if (isLoading) {
      return 'Loading...'
    } else {
      return 'No matching records found'
    }
  } else {
    return 'Type at least 4 characters to search'
  }
}

/**
 * Filters out matching records for user name provided from existing search results
 * @param newUserName user name to use for filtering
 * @param searchResults existing search results
 * @returns filtered out matching records
 */
export const generateFilteredSelectList = (
  newUserName: string,
  searchResults: Array<any>,
) => {
  let filteredDisplayList = Array<string>()
  for (var user of searchResults) {
    //Skip users that don't have a Member ID
    if (user.memberMlsId != null) {
      // Ensure that no properties are ever null
      user.name = user.name || `${user.firstName} ${user.lastName}`
      user.officeMlsId = user.officeMlsId || ''

      if (
        user.login.indexOf(newUserName) !== -1 ||
        user.name.indexOf(newUserName) !== -1 ||
        user.memberMlsId.indexOf(newUserName) !== -1
      ) {
        filteredDisplayList.push(user)
      }
    }
  }
  return filteredDisplayList
}

/**
 * Formats given value correctly for displaying in autocomplete control
 * @param option value to format
 * @returns formatted value
 */
export const getOptionLabel = (option: any) => {
  return `${option.name} - ${option.officeMlsId} (${option.memberMlsId})`
}

export const processUserNameChange = (
  reason: string,
  value: string,
  lastUserSearch: string,
  lastUserSearchResults: any[],
  userSearchLimit: number,
  accessToken: string,
  setUserNameSearch: any,
  setFilteredUserList: any,
  setAutoCompleteOpen: any,
  setLastUserSearch: any,
  setLoading: any,
  setLastUserSearchResults: any,
  setErrorAlertMessage: any,
  processErrorMsg: any,
) => {
  // Don't trigger a new search if the user has selected a value from the list
  if (reason !== 'reset') {
    setUserNameSearch(value)

    // Check if the username is long enough to start searching
    if (value != null && value.length > 3) {
      // Check if we can reuse the previous search results
      if (
        lastUserSearch.length > 0 &&
        value.substr(0, lastUserSearch.length) === lastUserSearch &&
        lastUserSearchResults.length < userSearchLimit
      ) {
        // Generate the filtered results from the previous results set
        setFilteredUserList(
          generateFilteredSelectList(value, lastUserSearchResults),
        )
        setAutoCompleteOpen(true)
      } else {
        setLastUserSearch(value)
        setLoading(true)

        storage.setUser(value)

        // Generate server request to search for users
        const requestOptions = {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: accessToken,
          },
        }
        const url =
          process.env.REACT_APP_OKTA_BRIGHT_ORG_ENDPOINT +
          '/getOktaUserList?search=' +
          value
        fetch(url, requestOptions)
          .then(async (response) => {
            const data = await response.json()

            // check for error response
            if (!response.ok) {
              // get error message from body or default to response status
              const error =
                (data && data.message) ||
                'Error Response Status: ' + response.status

              setLastUserSearch('')
              setLastUserSearchResults([])

              setErrorAlertMessage(error)

              processErrorMsg(error.message, 'Impersonate', 'User name change')
            } else {
              setLastUserSearchResults(data)
              setFilteredUserList(generateFilteredSelectList(value, data))
              setAutoCompleteOpen(true)
            }
            setLoading(false)
          })
          .catch((error) => {
            setLoading(false)
            setLastUserSearch('')
            setLastUserSearchResults([])

            setErrorAlertMessage(error.message)
            processErrorMsg(
              error.message,
              'Impersonate',
              `User name change. Unable to process api ${url}`,
            )
          })
      }
    } else {
      setFilteredUserList([])
      setLastUserSearch('')
      setLastUserSearchResults([])
      setErrorAlertMessage('')
    }
  }
}

export enum CountryChangeMode {
  MANUALLY = 1,
  AUTOMATICALLY = 2,
}

export const isMultiLoginApp = () =>
  process.env.REACT_APP_MULTI_LOGIN === 'true'
